'use client';

import { useLocale, useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { EventIdentity, type EventIdentityAsset } from '@/components/live/event-identity';
import type { LiveEventSummary } from '@/components/live/types';
import { Countdown } from '@/components/ui/countdown';
import { Icon } from '@/components/ui/icon';
import { Cancel01Icon, Timer01Icon } from '@/components/ui/icon/glyphs';
import { NavLink } from '@/components/ui/navigation-list';
import { Panel } from '@/components/ui/panel';
import { StatusPulse } from '@/components/ui/status-pulse';
import { useFormattedCurrency } from '@/lib/currency/use-formatted-currency';

export interface EventTimeCardProps {
  variant: 'live' | 'proposal' | 'didNotOpen';
  event: LiveEventSummary;
  eventLogo: EventIdentityAsset | null;
  participantLogos?: readonly EventIdentityAsset[] | null;
  /** Server-corrected current time supplied by the live-event query owner. */
  initialNowMs: number;
  totalBackedD6?: string | undefined;
  goalD6?: string | undefined;
}

const EVENT_TITLE_CLASSNAME =
  'whitespace-normal font-semibold text-foreground-primary hover:text-foreground-brand @min-[480px]/event-time:truncate @min-[480px]/event-time:text-[15px]'; // ARBITRARY: approved UI 04 desktop title is 15px while its mobile title uses the 14px body token.
const COUNTDOWN_CLASSNAME =
  'font-mono text-[23px] font-bold leading-[26px] tracking-[-0.035em] text-foreground-primary @min-[480px]/event-time:text-[28px] @min-[480px]/event-time:leading-[30px]'; // ARBITRARY: approved UI 04 countdown uses 23/26 mobile and 28/30 desktop, between semantic display steps.

function DidNotOpenStatus({
  totalBackedD6,
  goalD6,
}: Pick<EventTimeCardProps, 'totalBackedD6' | 'goalD6'>): ReactNode {
  const t = useTranslations('liveEvents.eventTimeCard');
  const { formatD6 } = useFormattedCurrency();
  const backingLabel =
    totalBackedD6 !== undefined && goalD6 !== undefined
      ? t('backedGoal', {
          backed: formatD6(totalBackedD6, 'compact'),
          goal: formatD6(goalD6, 'compact'),
        })
      : null;

  return (
    <div className="flex shrink-0 flex-col items-end gap-0.25 text-right tabular-nums">
      {backingLabel === null ? null : (
        <span className="text-caption text-foreground-secondary">{backingLabel}</span>
      )}
      <strong className="text-heading-sm font-semibold text-feedback-error-content">
        {t('didNotReachGoal')}
      </strong>
    </div>
  );
}

function formatLocalEventTime(value: string, locale: string): string {
  const date = new Date(value);
  const day = new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(date);
  const time = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
  return `${day} ${time}`;
}

export function EventTimeCard({
  variant,
  event,
  eventLogo,
  participantLogos,
  initialNowMs,
  totalBackedD6,
  goalD6,
}: EventTimeCardProps): ReactNode {
  const locale = useLocale();
  const t = useTranslations('liveEvents.eventTimeCard');
  const tCountdown = useTranslations('liveEvents.countdown');
  const isProposal = variant !== 'live';
  const didNotOpen = variant === 'didNotOpen';
  const target = isProposal ? event.start : event.end;
  const targetMs = Date.parse(target);
  const stateLabel = didNotOpen
    ? t('didNotOpenState')
    : isProposal
      ? t('proposalState')
      : t('liveState');
  const cardAriaLabel = didNotOpen
    ? t('didNotOpenAria')
    : isProposal
      ? t('proposalAria')
      : t('liveAria');
  const targetDateLabel = isProposal
    ? t('eventStarts', { date: formatLocalEventTime(target, locale) })
    : t('eventEnds', { date: formatLocalEventTime(target, locale) });
  const countdownHeading = isProposal ? t('liveEventStartsIn') : t('resolvesIn');

  return (
    <Panel
      data-testid="event-time-card"
      role="group"
      aria-label={cardAriaLabel}
      density="flush"
      className="@container/event-time overflow-hidden"
    >
      <div className="flex h-9 items-center gap-1.75 border-b border-border-secondary px-3 text-caption font-semibold text-foreground-secondary @min-[480px]/event-time:h-10 @min-[480px]/event-time:px-4">
        <Icon
          icon={didNotOpen ? Cancel01Icon : Timer01Icon}
          className="size-3.5 text-foreground-tertiary"
        />
        <span>{t('title')}</span>
        <StatusPulse
          aria-label={stateLabel}
          tone={didNotOpen ? 'neutral' : isProposal ? 'warning' : 'error'}
          className="ml-0.25"
        />
      </div>
      <div className="flex min-w-0 items-center justify-between gap-3 p-3 @min-[480px]/event-time:gap-5 @min-[480px]/event-time:px-4 @min-[480px]/event-time:py-3.5">
        <div className="flex min-w-0 flex-1 items-center gap-2 @min-[480px]/event-time:gap-3">
          <EventIdentity
            eventLogo={eventLogo}
            participantLogos={participantLogos}
            size="event-time-card"
          />
          <div className="flex min-w-0 flex-col gap-0.5">
            <NavLink
              href={`/live/${encodeURIComponent(event.id)}`}
              prefetch={false}
              className={EVENT_TITLE_CLASSNAME}
            >
              {event.title}
            </NavLink>
            <span className="hidden text-caption text-foreground-tertiary @min-[480px]/event-time:block">
              {targetDateLabel}
            </span>
          </div>
        </div>
        {didNotOpen ? (
          <DidNotOpenStatus totalBackedD6={totalBackedD6} goalD6={goalD6} />
        ) : (
          <div className="flex shrink-0 flex-col items-end gap-0.25 text-right tabular-nums">
            <span className="text-caption text-foreground-secondary">{countdownHeading}</span>
            <Countdown
              targetMs={targetMs}
              initialNowMs={initialNowMs}
              endedLabel={tCountdown('ended')}
              ariaLabel={(countdown: string) =>
                isProposal
                  ? t('startCountdownLabel', { countdown })
                  : t('countdownLabel', { countdown })
              }
              className={COUNTDOWN_CLASSNAME}
            />
          </div>
        )}
      </div>
    </Panel>
  );
}
